// @ts-nocheck
import { useState, useMemo, useRef, useContext } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Text,
  Flex,
  IconButton,
  Button,
  Center,
  useColorModeValue,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuGroup,
  MenuOptionGroup,
  MenuDivider,
  Link,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { useTable, useSortBy } from "react-table";
import { FiMoreHorizontal, FiMoreVertical } from "react-icons/fi";
import moment from "moment";
import { CSVLink } from "react-csv";
import { HiOutlineLightningBolt } from "react-icons/hi";
import { AiOutlineEye } from "react-icons/ai";
import toast from "react-hot-toast";
import { useSession } from "next-auth/react";
import PuffLoader from "react-spinners/PuffLoader";
import Tweet from "components/tweets/masonry/tweet";
import * as analytics from "utils/analytics";
import { generate } from "controllers/variations";
import { TweetContext } from "context/tweetContext";
import { getAccount } from "utils/sessionHelper";
import { useOnClickOutside } from "utils/helpers";
import numbro from "numbro";
import { isAllowed, getPlan } from "../../controllers/subscription";
import { PreviewText } from "components/preview-text"
import { formatTweetFromPost } from "../../utils/linkedinUtils";
import { customDateSort, getExactPostDate, customEngagementPercentSort } from "./utils";
import { layerStyle, textStyle, variant } from "theme/names";
import { mapPostsPerformance } from "utils/mapPostPerformance";

let maxImpression = 0;
let maxEngagement = 0;
let maxEngagementPercent = 0;
let maxLikes = 0;
let maxReplies = 0;
let maxShares = 0;

export function TweetPerformance({ stats = [], isLoading = false, disableExport = false }) {

  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"
  const csvLink = useRef(null);

  const data = useMemo(
    () => {
      if (isLoading) return [];
      const mappedPostPerformance = mapPostsPerformance(stats, getAccount(session));
      maxImpression = mappedPostPerformance.maxImpression;
      maxEngagement = mappedPostPerformance.maxEngagement;
      maxEngagementPercent = mappedPostPerformance.maxEngagementPercent;
      maxLikes = mappedPostPerformance.maxLikes;
      maxReplies = mappedPostPerformance.maxReplies;
      maxShares = mappedPostPerformance.maxShares;
      return mappedPostPerformance.stats
    },
    [stats, isLoading]
  );

  // console.log("maxImpression: " + maxImpression);

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date",
        tooltipText: "Date",
        sortDescFirst: true,
        Cell: ({ value, row }) => (
          <Text as={Link} href={row.original.url} target="_blank" textStyle={textStyle["body.medium.light"]} w="162px" textAlign="left">
            {value}
          </Text>
        ),
        sortType: customDateSort
      },
      {
        Header: "Text & Labels",
        accessor: "previewText",
        tooltipText: "Text & Labels",
        disableSortBy: true,
        Cell: PreviewText,
      },
      {
        Header: "Views.",
        accessor: "impression",
        tooltipText: "Impressions",
        isNumeric: true,
        sortDescFirst: true,
        Cell: ({ value }) => (
          <Text textStyle={textStyle["body.medium.light"]} fontWeight={value && maxImpression == value ? "900" : "400"}>
            {numbro(value)?.format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: "Engag.",
        accessor: "engagement",
        tooltipText: "Engagement",
        isNumeric: true,
        sortDescFirst: true,
        Cell: ({ value }) => (
          <Text textStyle={textStyle["body.medium.light"]} fontWeight={value && maxEngagement == value ? "900" : "400"}>
            {(Boolean(value) === true) ? numbro(value)?.format({ thousandSeparated: true }) : 0}
          </Text>
        ),
      },
      {
        Header: "Engag. %",
        accessor: "engagementPercent",
        tooltipText: "Engagement Percentage",
        sortDescFirst: true,
        Cell: ({ value }) => (
          <Text textStyle={textStyle["body.medium.light"]} fontWeight={value && maxEngagementPercent == value ? "900" : "400"}>
            {value.toFixed(2) + "%"}
          </Text>
        ),
        sortType: customEngagementPercentSort
      },
      {
        Header: "Likes",
        accessor: "likes",
        tooltipText: "Likes",
        isNumeric: true,
        sortDescFirst: true,
        Cell: ({ value }) => (
          <Text textStyle={textStyle["body.medium.light"]} fontWeight={value && maxLikes == value ? "900" : "400"}>
            {numbro(value)?.format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: "Replies",
        accessor: "replies",
        tooltipText: "Replies",
        isNumeric: true,
        sortDescFirst: true,
        Cell: ({ value }) => (
          <Text textStyle={textStyle["body.medium.light"]} fontWeight={value && maxReplies == value ? "900" : "400"}>
            {numbro(value)?.format({ thousandSeparated: true })}
          </Text>
        ),
      },
      // {
      //   Header: "Shares",
      //   accessor: "shares",
      //   tooltipText: "Shares",
      //   isNumeric: true,
      //   sortDescFirst: true,
      //   Cell: ({ value }) => (
      //     <Text textStyle={textStyle["body.medium.light"]} fontWeight={value && maxShares == value ? "900" : "400"}>
      //       {numbro(value)?.format({ thousandSeparated: true })}
      //     </Text>
      //   ),
      // },
      {
        Header: "",
        accessor: "ai",
        tooltipText: "",
        disableSortBy: true,
        Cell: AiIcon,
      },
    ],
    []
  );

  let headers = [{ label: "Username", key: "username" }];

  headers = headers.concat(columns
    ?.map(x => { return { label: x.Header, key: x.accessor } })
    ?.filter(x => x.key !== "ai" && x.key !== "previewText"));


  headers.push({ label: "Text", key: "previewText.textEscaped" });
  headers.push({ label: "Media", key: "media" });
  headers.push({ label: "Post Url", key: "url" });
  headers.push({ label: "Labels", key: "previewText.labels" });
  // console.log(data);
  // console.log(headers);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: {
          sortBy: [
            {
              id: 'date',
              desc: false
            }
          ]
        }
      }, useSortBy);

  return (
    <Box>
      <Heading fontSize="xl">Detailed Posts performance</Heading>
      {
        !disableExport && (
          <Flex justifyContent="flex-end">
            <Menu variant={variant.Menu.secondary}>
              <MenuButton
                variant={variant.Button.secondary}
                as={IconButton} size="sm" ml={2} icon={<FiMoreHorizontal fontSize="14px" />} />
              <MenuList w="100%">
                <CSVLink
                  css={{ width: "100%" }}
                  data={data}
                  headers={headers}
                  filename={"posts_stats.csv"}
                  className="hidden"
                  ref={csvLink}
                  target="_blank"
                >
                  <MenuItem w="100%">
                    Export CSV
                  </MenuItem>
                </CSVLink>
              </MenuList>
            </Menu>
          </Flex>
        )
      }
      <Box
        mt="2"
        // border="1px"
        layerStyle={layerStyle["bg.border.rounded"]}
        // borderColor={useColorModeValue("gray.100", "whiteAlpha.300")}
        // borderRadius="xl"
        p="4"
        // pb="0"
        // width="fit-content"
        maxWidth="100%"
        overflowX="scroll"
        position="relative"
        ml={{ base: "0", md: "6" }}
      >
        <Table {...getTableProps()}
          variant={variant.Table.simple}
          fontSize="xs">
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({ title: undefined })
                    )}
                    paddingX="1"
                  >
                    <Tooltip label={column.tooltipText} key={index}>
                      <Box>
                        {column.render("Header")}
                        <chakra.span pl="1.5">
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <TriangleDownIcon aria-label="sorted descending" />
                            ) : (
                              <TriangleUpIcon aria-label="sorted ascending" />
                            )
                          ) : null}
                        </chakra.span>
                      </Box>
                    </Tooltip>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <Td
                      {...cell.getCellProps()}
                      paddingX="1"
                      textAlign="center"
                    >
                      {cell.render("Cell")}
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        {!isLoading && stats.length <= 0 && (
          <Text textStyle={textStyle["body.medium.light"]} p="8" textAlign="center">
            No Post found. Please try a different timeframe
          </Text>
        )}
        {isLoading && (
          <Center pt={10} h="400px">
            <PuffLoader
              color={"#0A66C2"}
              loading={isLoading}
              cssOverride={{ display: "block", margin: "0 auto", borderColor: "red" }}
              size={50}
            />
          </Center>
        )}
      </Box>
    </Box>
  );
}

function AiIcon({ value: stat }) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const tweetContext = useContext(TweetContext);
  const { data: session } = useSession() ?? {}
  stat = formatTweetFromPost(stat);

  if (!session?.user?.uid) {
    return (
      <></>
    )
  }

  return (
    <IconButton
      size="sm"
      cursor="pointer"
      variant={variant.Button.actionGray}
      icon={<HiOutlineLightningBolt size={"1.2em"} />}
      isLoading={isLoading}
      onClick={async () => {
        
        if (
          session?.user?.data?.skipPopupVariationAuto &&
          (session?.user?.data?.creditsAuto ||
            isAllowed(session.user, "ai_variation"))
        ) {
          setIsLoading(stat.id);
          let result = await generate(
            session,
            stat,
            1,
            getAccount(session)?.tempLevel
          );
          setIsLoading("");

          if (result?.success && result?.data?.variations?.length) {
            let newVariation = result?.data?.variations[0];
            //@ts-ignore
            tweetContext.newTweet({ text: newVariation });
            //@ts-ignore
            tweetContext.open();
            toast.success("Variation pasted in composer");
          }
        } else {
          //@ts-ignore
          tweetContext.requestVariationAuto(stat);
        }
      }}
    />
  );
}
